import React, { useCallback } from "react";
import { Alert, Platform } from "react-native";

import InvitationItemView from "./InvitationItemView";

import {
  InvitationsInvitationFragment,
  InvitationsListMyOrganiationsSentInvitationsDocument,
  InvitationsListMyOrganiationsSentInvitationsQuery,
  InvitationsListMySentInvitationsDocument,
  InvitationStatus,
  useInvitationsDeleteInvitationMutation,
  useInvitationsResendInvitationMutation,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useToastNotification } from "@/hooks/useToastNotification";
import { formatError } from "@/utils/formatError";
import formatPhoneNumber from "@/utils/formatPhoneNumber";
import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";

/**
 * Props for the InvitationItemContainer component.
 */
interface Props {
  item: InvitationsInvitationFragment;
}

/**
 * Container component for managing a single invitation item with resend and delete actions.
 * @param root0 - The component props
 * @param root0.item - The invitation data fragment containing invitation details, receiver, sender, role, and status information
 */
const InvitationItemContainer = ({ item }: Props) => {
  const toast = useToastNotification();

  // SE-6562: both name attributes are optional in Cognito. The sender path used
  // to interpolate them unguarded and rendered "undefined undefined"; both
  // paths now share the full name → email → fallback chain. The receiver's
  // fallback stays the invited phone number, which is that invitation's
  // identity; the sender has no phone on the fragment, so it uses a placeholder.
  const senderNameDisplay = getCognitoDisplayName(
    item.user?.cognitoUser?.Attributes,
    "Unknown User"
  );

  const recordResendGoal = useRecordUserGoal({
    goalType: GoalType.ResendInvitation,
    value: item.user?.id ?? "",
  });
  const recordDeleteGoal = useRecordUserGoal({
    goalType: GoalType.DeleteInvitation,
    value: item.user?.id ?? "",
  });

  const nameDisplay = getCognitoDisplayName(
    item.receiver?.cognitoUser?.Attributes,
    formatPhoneNumber(item.phoneNumber, true)
  );

  const [resendInvitation, { loading: resendLoading }] =
    useInvitationsResendInvitationMutation({
      onCompleted: () => {
        recordResendGoal();
        toast.show({
          action: "success",
          description: "Resend invitation successful",
        });
      },
      onError: error => {
        const e = formatError(error);
        toast.show({ action: "danger", description: e.message });
      },
      variables: { input: { id: item.id } },
    });

  const [deleteInvitation, { loading: deleteLoading }] =
    useInvitationsDeleteInvitationMutation({
      onCompleted: () => {
        recordDeleteGoal();
        toast.show({
          action: "success",
          description: "Delete invitation successful",
        });
      },
      onError: error => {
        const e = formatError(error);
        toast.show({ action: "danger", description: e.message });
      },
      refetchQueries: [{ query: InvitationsListMySentInvitationsDocument }],
      variables: { input: { id: item.id } },
      update: (cache, { data, errors }) => {
        const existingData: InvitationsListMyOrganiationsSentInvitationsQuery | null =
          cache.readQuery({
            query: InvitationsListMyOrganiationsSentInvitationsDocument,
            variables: { status: InvitationStatus.Pending },
          });
        const temporaryData = data?.deleteInvitation;
        if (!!existingData && !!temporaryData && !errors) {
          cache.writeQuery({
            query: InvitationsListMyOrganiationsSentInvitationsDocument,
            variables: { status: InvitationStatus.Pending },
            data: {
              listMyOrganizationsSentInvitations: {
                ...existingData.listMyOrganizationsSentInvitations,
                edges:
                  existingData.listMyOrganizationsSentInvitations.edges.filter(
                    item => item.node.id !== temporaryData.id
                  ),
              },
            },
          });
        }
      },
      optimisticResponse: {
        __typename: "Mutation",
        deleteInvitation: item,
      },
    });

  const handleDelete = useCallback(() => {
    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to delete this invitation?`
      );
      return doDelete ? deleteInvitation() : null;
    } else {
      return new Promise((resolve, reject) =>
        Alert.alert(
          `Delete Invitation`,
          `Are you sure you want to delete this invitation?`,
          [
            {
              text: "Cancel",
              style: "cancel",
            },
            {
              text: "Yes",
              onPress: () => deleteInvitation().then(resolve).catch(reject),
              style: "destructive",
            },
          ]
        )
      );
    }
  }, [deleteInvitation]);

  const handleResend = useCallback(() => {
    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to resend this invitation?`
      );
      return doDelete ? resendInvitation() : null;
    } else {
      return new Promise((resolve, reject) =>
        Alert.alert(
          `Resend Invitation`,
          `Are you sure you want to resend this invitation?`,
          [
            {
              text: "Cancel",
              style: "cancel",
            },
            {
              text: "Yes",
              onPress: () => resendInvitation().then(resolve).catch(reject),
              style: "destructive",
            },
          ]
        )
      );
    }
  }, [resendInvitation]);

  const loading = resendLoading || deleteLoading;

  return (
    <InvitationItemView
      item={item}
      nameDisplay={nameDisplay}
      senderNameDisplay={senderNameDisplay}
      loading={loading}
      onDelete={handleDelete}
      onResend={handleResend}
    />
  );
};

export default InvitationItemContainer;
